import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { styled } from "@mui/system";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useLazyGetChatListQuery } from "services/main";
import { useLazyGetChatHistoryQuery } from "services/main";
import { connectChatSocket } from "utils/socket";
import useAuth from "hooks/useAuth";
import ShowToast from "components/MDToast";
import moment from "moment";

// Styled components
const Root = styled("div")({
  display: "flex",
  height: "100vh",
});

const Inbox = styled(Card)({
  width: "310px",
  minWidth: "310px",
  borderRadius: 0,
  display: "flex",
  flexDirection: "column",
});

const InboxHeader = styled("div")({
  padding: "16px",
  backgroundColor: "#e0e0e0",
});

const InboxContent = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "8px",
});

const ConversationButton = styled(Button)({
  justifyContent: "space-between",
  textTransform: "none",
  padding: "8px",
  width: "100%",
});

const MessageArea = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
});

const MessageList = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "16px",
});

const MessageInput = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "16px",
});

let socket = null;

export default function QuoteChat({ sessionId }) {
  const user = useAuth();
  const sectionRef = useRef(null);
  const [chatList] = useLazyGetChatListQuery();
  const [chatHistory] = useLazyGetChatHistoryQuery();
  const [chatListData, setChatListData] = useState([]);
  const [chatHistoryData, setChatHistoryData] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState("");
  console.log(message, "message");
  console.log(sessionId, "sessionId");

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  //getting chat list
  const getAllChats = async () => {
    try {
      const response = await chatList({}).unwrap();
      if (response?.status === 200) {
        setChatListData(response?.data);
      } else {
        setChatListData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //getting chat history
  const getChatHistory = async (session) => {
    try {
      const response = await chatHistory(session).unwrap();
      if (response?.status === 200) {
        setChatHistoryData(response?.data);
      } else {
        setChatHistoryData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents adding a new line
      if (message.trim().length > 0) {
        sendMessage();
      } else {
        ShowToast("Please write something", "error");
      }
    }
  };

  //message listener
  const listenToMessage = () => {
    socket?.addEventListener("message", (event) => {
      try {
        const parsedData = JSON.parse(event.data); // Parse the string to a JavaScript object
        console.log("Parsed chat message:", parsedData);
        setChatHistoryData((prev) => [...prev, parsedData]);
        setTimeout(() => {
          scrollToSection();
        }, 500);
      } catch (error) {
        console.error("Failed to parse message data:", error);
        console.log("Raw message data:", event.data); // Keep a log of the raw data
      }
    });
  };

  //connecting to socket and chat
  const handleClick = (session) => {
    socket?.close();
    setSelectedId(session);
    setTimeout(() => {
      scrollToSection();
    }, 500);

    socket = connectChatSocket(user?.id || "", session || "");
    if (socket) {
      console.log("connected");
      listenToMessage();
    }
    getChatHistory(session);
  };

  //send message
  const sendMessage = async () => {
    setMessage("");
    socket?.send(
      JSON.stringify({
        type: "chat",
        message: message,
      })
    );
    setTimeout(() => {
      scrollToSection();
    }, 500);
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToSection();
    }, 500);

    socket = connectChatSocket(user?.id || "", sessionId || "");
    if (socket) {
      console.log("connected");
      listenToMessage();
    }
    getChatHistory(sessionId);
  }, []);

  useEffect(() => {
    getAllChats();
  }, []);

  return (
    <Root sx={{ maxHeight: 500, marginTop: 2 }}>
      {/* Right side: Message Area */}
      <MessageArea>
        <MessageList>
          {/* Sample messages */}
          {chatHistoryData?.map((item) => {
            return (
              <div key={item?.id}>
                {item?.sender === user?.id ? (
                  <Card
                    sx={{
                      width: "fit-content",
                      maxWidth: "70%",
                      marginLeft: "auto",
                      backgroundColor: "#2B604C",
                      color: "white",
                      marginBottom: 2,
                      textAlign: "left",
                      paddingBottom: 0,
                    }}
                  >
                    <CardContent sx={{ paddingBottom: 0 }}>
                      <MDTypography variant="body2" color="white">
                        {item?.user_input || "-"}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                        color="white"
                      >
                        {moment(item.timestamp).format("h:mm A") || "-"}
                      </MDTypography>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      maxWidth: "70%",
                      width: "fit-content",
                      marginBottom: 2,
                    }}
                  >
                    <CardContent>
                      <MDTypography variant="body2">
                        {item?.user_input || "-"}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                      >
                        {moment(item.time_stamp).format("h:mm A") || "-"}
                      </MDTypography>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
          <div ref={sectionRef} />
        </MessageList>

        <MessageInput>
          <MDInput
            fullWidth
            placeholder="Type your message..."
            variant="outlined"
            size="small"
            type="text"
            value={message}
            onKeyDown={handleKeyPress}
            onChange={(val) => setMessage(val.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#2B604C", // Default border color
                },
                "&:hover fieldset": {
                  borderColor: "#2B604C", // Border color on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2B604C", // Border color when focused
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "black", // Placeholder color
                },
              },
            }}
          />
          <MDButton
            sx={{ marginLeft: 2, backgroundColor: "#2B604C" }}
            variant="gradient"
            color="success"
            onClick={() => {
              if (message?.length) {
                sendMessage();
              } else {
                ShowToast("Please write something", "error");
              }
            }}
          >
            <Send />
          </MDButton>
        </MessageInput>
      </MessageArea>
    </Root>
  );
}
